import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { SocialAuthService } from '@abacritt/angularx-social-login';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, finalize } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { CoreService } from 'src/app/services/core.service';
import { AuthGoogleSigninButtonComponent } from '@features/auth/presentation/components/auth-google-signin-button/auth-google-signin-button.component';
import { AuthService } from '@features/auth/services/auth.service';
import { clearOnboardDraft } from '@features/onboard/presentation/utils/onboard-draft.storage';
import { ToastService } from '@shared/ui/toast/toast.service';
import {
  authErrorI18nKey,
  resolveAuthRequestErrorKind,
} from '@features/auth/utils/auth-request-error.util';
import {
  canonicalizeWebsitePlanCode,
  patchWebsiteClaim,
  readWebsiteClaim,
  type WebsitePlanCode,
} from '@features/website/public-api';
import { navigateAfterStaffAuth } from '@features/auth/utils/navigate-after-staff-auth.util';
import { setCreateBusinessIntent } from '@core/auth/create-business-intent.storage';
import { SessionUser } from '@core/auth/auth-session.model';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import { LegalPublicService } from '@features/legal/public-api';
import { LocationContextService } from '@core/tenant/location-context.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';

@Component({
  selector: 'app-side-register',
  imports: [
    RouterModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    AuthGoogleSigninButtonComponent,
    TranslateModule,
    TablerIconsModule,
  ],
  templateUrl: './side-register.component.html',
})
export class AppSideRegisterComponent implements OnInit, OnDestroy {
  private readonly settings = inject(CoreService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly socialAuthService = inject(SocialAuthService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly legalPublic = inject(LegalPublicService);

  options = this.settings.getOptions();
  private authSub?: Subscription;

  showGoogleSignIn = false;
  googleAuthInitializing = true;
  hidePassword = true;
  isSubmitting = false;
  errorMessage = '';
  emailAlreadyRegistered = false;
  validationAttempted = false;
  claimBusinessName = '';
  claimSlug = '';
  readonly passwordRules = ['length', 'uppercase', 'lowercase', 'number'] as const;

  get loginWithCreateBusinessQuery(): { email?: string; intent: string } {
    const email = (this.form.value.email ?? '').trim();
    return email
      ? { email, intent: 'create_business' }
      : { intent: 'create_business' };
  }

  ngOnInit(): void {
    // Registro = alta de negocio propio. Evita aterrizar en un tenant viejo tras Google OAuth.
    setCreateBusinessIntent();

    const emailParam = this.route.snapshot.queryParamMap.get('email');
    if (emailParam?.trim()) {
      this.form.patchValue({ email: emailParam.trim() });
    }

    const nameParam = this.route.snapshot.queryParamMap.get('name')?.trim() ?? '';
    const slugParam = this.route.snapshot.queryParamMap.get('slug')?.trim() ?? '';
    const planParam = this.route.snapshot.queryParamMap.get('plan');
    const existingClaim = readWebsiteClaim();
    const planFromQuery = canonicalizeWebsitePlanCode(planParam);
    const planFromClaim = canonicalizeWebsitePlanCode(existingClaim?.planCode);
    // No pisar un claim Vender/Emprende/Opera con default link si falta ?plan=.
    const planCode: WebsitePlanCode = planFromQuery || planFromClaim || 'link';
    if (nameParam && slugParam) {
      patchWebsiteClaim({ name: nameParam, slug: slugParam, planCode });
    } else {
      patchWebsiteClaim({ planCode });
    }
    const claim = readWebsiteClaim();
    this.claimBusinessName = nameParam || claim?.name?.trim() || '';
    this.claimSlug = slugParam || claim?.slug?.trim() || '';

    void this.initGoogleAuth();
  }

  private async initGoogleAuth(): Promise<void> {
    try {
      await this.socialAuthService.signOut();
    } catch {
      // Sin sesión Google activa; continuar.
    }

    this.googleAuthInitializing = false;
    this.showGoogleSignIn = true;
    this.authSub = this.socialAuthService.authState
      .pipe(distinctUntilChanged((prev, next) => prev?.idToken === next?.idToken))
      .subscribe((user) => {
        if (!user?.idToken || this.isSubmitting) {
          return;
        }
        this.isSubmitting = true;
        this.errorMessage = '';
        // Reafirmar intent por si el usuario limpió storage entre tanto.
        setCreateBusinessIntent();
        this.authService.loginWithGoogle(user.idToken).subscribe({
          next: (result) => {
            this.isSubmitting = false;
            clearOnboardDraft();
            this.toast.successTranslated('auth.register.successToast');
            this.legalPublic.acceptServiceDocs('register_owner').subscribe({
              next: () => void this.navigateAfterAuth(result.user),
              error: () => void this.navigateAfterAuth(result.user),
            });
          },
          error: (err: unknown) => {
            this.isSubmitting = false;
            const kind = resolveAuthRequestErrorKind(err);
            this.errorMessage = this.translate.instant(authErrorI18nKey('auth.register', kind));
          },
        });
      });
  }

  ngOnDestroy(): void {
    this.authSub?.unsubscribe();
  }

  form = new FormGroup({
    uname: new FormControl('', [Validators.required, Validators.minLength(2)]),
    lname: new FormControl('', [Validators.required, Validators.minLength(2)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', [
      Validators.required,
      Validators.minLength(8),
      Validators.pattern(/[A-Z]/),
      Validators.pattern(/[a-z]/),
      Validators.pattern(/[0-9]/),
    ]),
    legalAccepted: new FormControl(false, [Validators.requiredTrue]),
  });

  get f() {
    return this.form.controls;
  }

  passwordMeets(rule: 'length' | 'uppercase' | 'lowercase' | 'number'): boolean {
    const password = this.form.controls.password.value ?? '';
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    }[rule];
  }

  showFieldError(control: FormControl): boolean {
    return (this.validationAttempted || control.dirty) && control.invalid;
  }

  submit(): void {
    this.validationAttempted = true;
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.errorMessage = '';
    this.emailAlreadyRegistered = false;
    this.isSubmitting = true;

    this.authService
      .register({
        name: this.form.value.uname ?? '',
        lastName: this.form.value.lname ?? '',
        email: this.form.value.email ?? '',
        password: this.form.value.password ?? '',
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (result) => {
          clearOnboardDraft();
          setCreateBusinessIntent();
          this.toast.successTranslated('auth.register.successToast');
          this.legalPublic.acceptServiceDocs('register_owner').subscribe({
            next: () => void this.navigateAfterAuth(result.user),
            error: () => void this.navigateAfterAuth(result.user),
          });
        },
        error: (err: unknown) => {
          const kind = resolveAuthRequestErrorKind(err);
          this.emailAlreadyRegistered = kind === 'email_already';
          this.errorMessage = this.translate.instant(authErrorI18nKey('auth.register', kind));
          if (this.emailAlreadyRegistered) {
            setCreateBusinessIntent();
          }
        },
      });
  }

  private navigateAfterAuth(user: SessionUser): Promise<boolean> {
    return navigateAfterStaffAuth({
      user,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
      adminShellMode: this.adminShellMode,
    });
  }
}
